/**
 * scoreadmin-fe permission namespace + constants (AUTHZ.md §5.4), card #2415.
 *
 * Follows the score-fe/src/permissions.js precedent: derive role/permission
 * checks from score-common's manifest (score-common/permissions) rather than
 * duplicating a literal, so a future role grant is picked up automatically.
 * scoreadmin-fe's own ScoreEntitlementGate.jsx already reads the access
 * token's `roles` claim directly instead of common-fe's usePermission() /
 * PermissionProvider, for the same reason score-fe's header explains:
 * account-api's pre-token-generation.js emits no `permissions` claim at all
 * (#276 never landed), so usePermission() fails closed for every real,
 * non-super user. hasAssignScribesPermission below matches that pattern.
 */
import { manifest } from "score-common/permissions";

export const PERMS = Object.freeze({
  ASSIGN_SCRIBES: "assign_scribes",
});

// Roles whose manifest permissions include assign_scribes — currently just
// score_admin (packages/score-common/src/permissions/index.js). Derived from
// the manifest, not hardcoded, so a future grant of assign_scribes to
// another role is picked up automatically.
export const ASSIGN_SCRIBES_ROLES = Object.freeze(
  Object.entries(manifest.roles)
    .filter(([, role]) => role.permissions.includes(PERMS.ASSIGN_SCRIBES))
    .map(([key]) => key)
);

/**
 * Pure predicate: does this user hold score:assign_scribes for the active
 * org? `rolesForOrg` is the decoded token's `roles[activeOrgId]` array
 * (qualified "score:score_admin" or bare "score_admin" — same two forms
 * ScoreEntitlementGate.jsx's activeOrgHasScoreAdminRole matches).
 *
 * Deliberately does NOT special-case a super user: score-api's route
 * (setClassPaperEntry, requirePermission('assign_scribes')) bypasses for
 * super, but every other per-field control on this screen (walk-trot-only,
 * DSHB override, ...) has no FE-side permission gate at all — this is the
 * first one card #2415 adds — and a genuine super admin who needs this can
 * hold score_admin for the org like anyone else, or call the route
 * directly. Keeping the FE check to the literal roles claim (no isSuper
 * bypass) keeps it simple and, unlike the app-shell gate, testable without
 * a super-only code path.
 */
export function hasAssignScribesPermission(rolesForOrg) {
  const roles = Array.isArray(rolesForOrg) ? rolesForOrg : [];
  return roles.some(
    (r) => ASSIGN_SCRIBES_ROLES.includes(r) || ASSIGN_SCRIBES_ROLES.includes(r?.split(":").pop())
  );
}
